/**
 * Tiny semver comparator — sufficient for our single use site (security update
 * threshold comparison). Avoids pulling in the 240KB `semver` package.
 *
 * Supports:
 *   - "X", "X.Y", "X.Y.Z" (missing parts default to 0)
 *   - Trailing pre-release ("-alpha") or build ("+sha") suffixes
 *   - Pre-release ranks lower than the same X.Y.Z release (1.0.0-rc1 < 1.0.0)
 *
 * Returns null when either input is malformed (callers must treat null as
 * "comparison unavailable" — typically by NOT locking the user).
 */

const parseCore = (v) => {
  if (typeof v !== 'string') return null;
  // Strip leading 'v' if present, then split off pre-release / build metadata
  const trimmed = v.trim().replace(/^v/i, '');
  if (!trimmed) return null;

  const dashIdx = trimmed.indexOf('-');
  const plusIdx = trimmed.indexOf('+');
  const suffixIdx = [dashIdx, plusIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const coreStr = suffixIdx === undefined ? trimmed : trimmed.slice(0, suffixIdx);
  const hasPreRelease = dashIdx >= 0 && (plusIdx < 0 || dashIdx < plusIdx);

  const parts = coreStr.split('.');
  if (parts.length === 0) return null;

  const nums = [];
  for (let i = 0; i < 3; i++) {
    const raw = parts[i];
    if (raw === undefined) {
      nums.push(0);
      continue;
    }
    if (!/^\d+$/.test(raw)) return null;
    nums.push(parseInt(raw, 10));
  }

  return { nums, hasPreRelease };
};

/**
 * compareSemver(a, b)
 *   -1 if a < b
 *    0 if a === b
 *    1 if a > b
 *   null if either is malformed
 */
export const compareSemver = (a, b) => {
  const pa = parseCore(a);
  const pb = parseCore(b);
  if (!pa || !pb) return null;

  for (let i = 0; i < 3; i++) {
    if (pa.nums[i] < pb.nums[i]) return -1;
    if (pa.nums[i] > pb.nums[i]) return 1;
  }
  // Cores equal — pre-release sorts below release
  if (pa.hasPreRelease && !pb.hasPreRelease) return -1;
  if (!pa.hasPreRelease && pb.hasPreRelease) return 1;
  return 0;
};

/**
 * isBelowMinVersion(current, minVersion)
 *   true  → user is below the required minimum (lock-eligible)
 *   false → user is at or above the required minimum, OR comparison failed
 *
 * Returning `false` on parse failure is the conservative choice: a malformed
 * field should never lock a user out of the app.
 */
export const isBelowMinVersion = (current, minVersion) => {
  const cmp = compareSemver(current, minVersion);
  return cmp === -1;
};
